@if (view === 'create') {
  <h2 mat-dialog-title class="dialog-title">New Meeting</h2>
  <mat-dialog-content>
    <div class="cd-dialog-layout">
      <form class="cd-meeting-fields" [formGroup]="form">
        <mat-form-field appearance="outline" class="cd-full-width">
          <mat-label>Title</mat-label>
          <input
            matInput
            formControlName="title"
            placeholder="What is this meeting about?"
            maxlength="80"
            data-testid="meeting-title-input"
          />
          @if (form.controls.title.touched && form.controls.title.hasError('required')) {
            <mat-error>A title is required</mat-error>
          }
        </mat-form-field>

        <mat-form-field appearance="outline" class="cd-full-width">
          <mat-label>Student</mat-label>
          <mat-select formControlName="studentUserId" data-testid="meeting-student-select">
            @for (student of students; track student.id) {
              <mat-option [value]="student.user_id">
                {{ displayName(student) }} &middot; Grade {{ student.grade_level }}
              </mat-option>
            }
          </mat-select>
          @if (students.length === 0) {
            <mat-hint>No students in your network yet</mat-hint>
          }
          @if (form.controls.studentUserId.touched && form.controls.studentUserId.invalid) {
            <mat-error>Choose a student</mat-error>
          }
        </mat-form-field>

        <div class="cd-field-row">
          <mat-form-field appearance="outline">
            <mat-label>Date</mat-label>
            <input
              matInput
              formControlName="date"
              [matDatepicker]="datePicker"
              [min]="minDate"
              [max]="pickerMaxDate"
              [matDatepickerFilter]="dateFilter"
              data-testid="meeting-date-input"
            />
            <mat-datepicker-toggle matIconSuffix [for]="datePicker" />
            <mat-datepicker #datePicker />
            @if (form.controls.date.touched && form.controls.date.invalid) {
              <mat-error>Pick a date</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Start Time</mat-label>
            <input
              matInput
              formControlName="startTime"
              [matTimepicker]="timePicker"
              [matTimepickerMin]="minStartTime"
              data-testid="meeting-time-input"
            />
            <mat-timepicker-toggle matIconSuffix [for]="timePicker" />
            <mat-timepicker #timePicker />
            @if (form.controls.startTime.touched && form.controls.startTime.invalid) {
              <mat-error>Pick a start time</mat-error>
            }
          </mat-form-field>

          <mat-form-field appearance="outline">
            <mat-label>Duration</mat-label>
            <mat-select formControlName="durationMinutes" data-testid="meeting-duration-select">
              @for (duration of durations; track duration) {
                <mat-option [value]="duration">{{ duration }} mins</mat-option>
              }
            </mat-select>
            @if (form.controls.durationMinutes.touched && form.controls.durationMinutes.invalid) {
              <mat-error>Pick a duration</mat-error>
            }
          </mat-form-field>
        </div>

        <mat-form-field appearance="outline" class="cd-full-width">
          <mat-label>Meeting Type</mat-label>
          <mat-select formControlName="meetingType" data-testid="meeting-type-select">
            <mat-option value="in-person">In Person</mat-option>
            <mat-option value="remote">Remote</mat-option>
          </mat-select>
        </mat-form-field>

        @if (form.controls.meetingType.value === 'remote') {
          <mat-form-field appearance="outline" class="cd-full-width">
            <mat-label>Meeting Link</mat-label>
            <input
              matInput
              formControlName="meetingLink"
              type="url"
              placeholder="https://..."
              data-testid="meeting-link-input"
            />
            <mat-hint>Paste the link from your calendar or video app</mat-hint>
          </mat-form-field>
        }

        <mat-form-field appearance="outline" class="cd-full-width">
          <mat-label>Description</mat-label>
          <textarea
            matInput
            formControlName="description"
            placeholder="Provide a description (optional)"
            rows="4"
            [maxlength]="descriptionLimit"
            data-testid="meeting-description-input"
          ></textarea>
          <mat-hint>{{ descriptionRemaining }} characters remaining</mat-hint>
        </mat-form-field>
      </form>
    </div>
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button data-testid="cancel-create-btn" mat-button type="button" (click)="dialogRef.close()">
      Cancel
    </button>
    <button
      data-testid="review-meeting-btn"
      mat-flat-button
      type="button"
      class="cd-create-btn"
      (click)="review()"
    >
      Continue
    </button>
  </mat-dialog-actions>
}

@if (view === 'confirm') {
  <h2 mat-dialog-title class="dialog-title">Create this meeting?</h2>
  <mat-dialog-content>
    <p class="cd-confirm-message">
      <strong>{{ form.controls.title.value }}</strong> with
      <strong>{{ selectedStudent ? displayName(selectedStudent) : '' }}</strong> on
      <strong>{{ scheduledStart | date: 'EEEE, MMMM d' }}</strong> at
      <strong>{{ scheduledStart | date: 'h:mm a' }}</strong>
      for {{ form.controls.durationMinutes.value }} minutes.
    </p>
    <p class="cd-confirm-note">
      This meeting is confirmed straight away — the student will be notified and will see it in
      their upcoming meetings.
    </p>
    @if (submitFailed()) {
      <p class="cd-confirm-error" role="alert" data-testid="create-error">
        <span>{{
          submitError() || "That didn't go through. Check the details and try again."
        }}</span>
        <button
          type="button"
          class="cd-dismiss-error-btn"
          data-testid="dismiss-error-btn"
          aria-label="Dismiss error"
          (click)="dismissError()"
        >
          <mat-icon>close</mat-icon>
        </button>
      </p>
    }
  </mat-dialog-content>

  <mat-dialog-actions align="end">
    <button
      data-testid="keep-editing-btn"
      mat-button
      type="button"
      [disabled]="submitting()"
      (click)="goToView('create')"
    >
      Keep editing
    </button>
    <button
      data-testid="confirm-create-btn"
      mat-flat-button
      type="button"
      class="cd-create-btn"
      [disabled]="submitting()"
      (click)="submit()"
    >
      {{ submitting() ? 'Creating…' : 'Create meeting' }}
    </button>
  </mat-dialog-actions>
}
